// vendors
import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  IonContent,
  ModalController,
  Platform,
  ViewWillEnter,
} from '@ionic/angular';
import { Observable, Subject, Subscription, combineLatest } from 'rxjs';
// common
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
// services
import { BetterBuysConstants } from '@app/better-buys/constants/better-buys-constants.enum';
import { BetterBuysPageViewModelV2 } from '@app/better-buys/models/better-buys.model';
import { BetterBuysService } from '@app/better-buys/services/better-buys.service';
import { ListGroupsAndItems } from '@app/lists/shared/list-detail-management/model/list-detail-management-view.model';
import { UserService } from '@app/user/services';
import { ShareOptions } from '@capacitor/share';
import { TranslateService } from '@ngx-translate/core';
import { SHARESHEET_CONSTANTS } from '@shared/constants/sharesheet-constants';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import { Product } from '@shared/models/product.model';
import { OrderItemService } from '@shared/services/order-item.service';
import {
  DeviceNotAvailableError,
  ShareSheetService,
  VersionError,
} from '@shared/services/sharesheet/sharesheet.service';
import { ToggleNewSearchService } from '@shared/services/toggle-new-search/toggle-new-search.service';
import { OrderItem } from '@usf/ngrx-order';
import { ProductPricingService, SelectPricingService } from '@usf/ngrx-pricing';
import {
  DivisionalPromotionsService,
  PERSONALIZATION_TYPE,
  ProductAnalyticsService,
  ProductTrackingInfo,
} from '@usf/ngrx-product';
import { SellerPriceDetails } from '@usf/price-types';
import { filter, finalize, map, take, tap } from 'rxjs/operators';
import { OrderService } from '../order/services/order.service';
import { HelpModalDesktopComponent } from '../shared/components/help-modal/help-modal-desktop/help-modal-desktop.component';
import { HelpModalTouchComponent } from '../shared/components/help-modal/help-modal-touch/help-modal-touch.component';
import { ModalWithNavComponent } from '../shared/components/modal-with-nav/modal-with-nav.component';
import { PageTitle } from '../shared/constants/page-titles.enum';
import { FEATURES } from '../shared/constants/splitio-features';
import {
  PwrRenderOptions,
  PwrRenderOptionsData,
} from '../shared/models/power-reviews/pwr-render-options';
import { QuantityUpdate } from '../shared/models/quantity-update';
import { ModalService } from '../shared/services/modal/modal.service';
import { PowerReviewHandlerCssService } from '../shared/services/power-review-handler-css.service';
import { PowerReviewService } from '../shared/services/power-review.service';
import { PDP_SHARESHEET_CONSTANTS } from './constants/constants';
import { ProductDetailViewModel } from './models/product-detail-view-model';
import { ProductToast } from './models/product-toast.model';
import { PowerReviewAnalyticsService } from './services/power-review-analytics.service';
import { ProductDetailPrintService } from './services/product-detail-print.service';
import { ProductDetailShareViaEmailService } from './services/product-detail-share-via-email.service';
import { ProductDetailToastService } from './services/product-detail-toast.service';
import { ProductDetailTrackPageLoadService } from './services/product-detail-track-page-load.service';
import { ProductDetailService } from './services/product-detail.service';

@Component({
  selector: 'app-product-detail',
  templateUrl: './product-detail.page.html',
  styleUrls: ['./product-detail.page.scss'],
})
export class ProductDetailPage implements ViewWillEnter {
  @ViewChild(IonContent) content: IonContent;
  product$: Observable<ProductDetailViewModel>;
  orderItem$: Observable<OrderItem>;
  alternativeOrderItem$: Observable<OrderItem>;
  ratingsObservable$: Observable<number>;
  sellerPriceDetails$: Observable<SellerPriceDetails>;
  isPmeSgpEnabled$: Observable<boolean>;
  divisionalPromotions$: Observable<string[]>;
  betterBuyViewModel$: Observable<BetterBuysPageViewModelV2>;
  isSuperUser$: Observable<boolean>;
  isGuestUser$: Observable<boolean>;

  selectedProductNumber: number;
  product: ProductDetailViewModel;
  isSellerCustomer = false;
  isCustomer = false;
  mainProductAttributesList = null;
  noQuestions = false;
  favoritesList: ListGroupsAndItems;
  isFavorite = false;
  isUpdatingFavoriteList = false;
  isFavoriteButtonEnabled$: Observable<boolean>;
  private selectedProductNumberRouteKey = 'selectedProductNumber';
  pwrRenderOptionsData: PwrRenderOptionsData;
  toast: ProductToast;

  isUsdaNutritionFlagOn$: Observable<boolean>;
  newSearchEnabled$: Observable<boolean>;
  isDynamicLastFourBuysLookbackEnabled$: Observable<boolean>;

  // subscriptions
  productSubscription$: Subscription = new Subscription();
  productChangeSubject = new Subject<number>();
  productChange$: Subscription;
  toast$: Subscription;
  deepLinkCustomer$: Subscription;
  personalizations$: Subscription;
  favoritesListSubscription$: Subscription;

  // feature flags
  productReviewsFeatureFlag = FEATURES.split_global_product_reviews.name;
  questionsAndAnswersFeatureFlag = FEATURES.split_global_product_questions.name;

  readonly PlatformEnum = PlatformEnum;

  constructor(
    readonly panAppState: PanAppState,
    private productDetailService: ProductDetailService,
    private productPricingService: ProductPricingService,
    private selectSellerPricingService: SelectPricingService,
    private route: ActivatedRoute,
    private readonly orderService: OrderService,
    readonly platform: Platform,
    readonly platformService: PlatformService,
    private powerReviewService: PowerReviewService,
    private productDetailTrackPageLoadService: ProductDetailTrackPageLoadService,
    private powerReviewAnalyticsService: PowerReviewAnalyticsService,
    private productDetailToastService: ProductDetailToastService,
    private modalService: ModalService,
    private modalController: ModalController,
    private shareSheetService: ShareSheetService,
    private productAnalyticsService: ProductAnalyticsService,
    private betterBuysService: BetterBuysService,
    private orderItemService: OrderItemService,
    private navigationHelperService: NavigationHelperService,
    private productDetailPrintService: ProductDetailPrintService,
    private productDetailShareViaEmailService: ProductDetailShareViaEmailService,
    private translateService: TranslateService,
    private userService: UserService,
    private readonly toggleNewSearchService: ToggleNewSearchService,
    private divisionalPromotionsService: DivisionalPromotionsService,
  ) {
    this.selectedProductNumber = parseInt(
      this.route.snapshot.data[this.selectedProductNumberRouteKey],
      10,
    );
  }

  goToTop() {
    this.content.scrollToTop();
  }

  ionViewWillEnter() {
    this.isSuperUser$ = this.userService.isSuperUser$();
    this.isGuestUser$ = this.userService.isGuestUser$();
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.productDetail),
    );
    this.personalizations$?.unsubscribe();

    this.personalizations$ = combineLatest([
      this.isGuestUser$,
      this.panAppState.customer$.pipe(filter(customer => !!customer)),
      this.productChangeSubject.asObservable().pipe(filter(x => !!x)),
    ])
      .pipe(take(1))
      .subscribe(([isGuestUser]) => {
        if (!isGuestUser) {
          this.productDetailService.sendPersonalizationEvent(
            PERSONALIZATION_TYPE.recentlyViewed,
            this.selectedProductNumber,
          );
        }
      });

    this.loadFavoritesList();

    this.productDetailService
      .loadProductDetail$(this.selectedProductNumber)
      .pipe(take(1))
      .pipe(
        finalize(() => {
          this.productSubscription$ = this.trackPageLoadAndSubstitutes();
        }),
      )
      .subscribe(_ => {
        this.betterBuysService.loadBetterBuyDetails(this.selectedProductNumber);
        this.product$ = combineLatest([
          this.productDetailService.getProductDetailViewModel(
            this.selectedProductNumber,
          ),
          this.isGuestUser$,
        ]).pipe(
          map(([product, isGuestUser]) => {
            this.mainProductAttributesList = product.trackingAttributes;
            this.product = product;
            this.productChangeSubject.next(this.selectedProductNumber);
            if (product?.alternative?.product?.productNumber && !isGuestUser) {
              this.productPricingService.getPrice(
                product.alternative.product.productNumber,
              );
              this.alternativeOrderItem$ =
                this.orderService.getOrderItemWithProductNumber$(
                  product.alternative.product.productNumber,
                );
            }

            if (product?.betterBuy?.product) {
              this.betterBuyViewModel$ =
                this.betterBuysService.selectBetterBuysViewModel$([
                  product.betterBuy.product.productNumber,
                ]);
            }
            return product;
          }),
        );
      });

    this.orderItem$ = this.orderService.getOrderItemWithProductNumber$(
      this.selectedProductNumber,
    );
    this.ratingsObservable$ =
      this.productDetailTrackPageLoadService.ratingStarts$;
    this.productChange$ = this.productChangeSubject
      .pipe(
        tap(async () => {
          await this.renderPowerReviews();
        }),
      )
      .subscribe();

    this.toast$ = this.productDetailToastService
      .getProductToasts$()
      .subscribe(toast => {
        this.toast = toast;
        this.goToTop();
      });

    this.isUsdaNutritionFlagOn$ = this.panAppState.feature$([
      FEATURES.split_global_usda_nutritional_panel.name,
    ]);

    this.isDynamicLastFourBuysLookbackEnabled$ = this.panAppState.feature$([
      FEATURES.split_dynamic_seller_lfb_lookback.name,
    ]);

    this.newSearchEnabled$ =
      this.toggleNewSearchService.newSearchEnabledObservable$;
  }

  ionViewWillLeave(): void {
    this.productSubscription$?.unsubscribe();
    this.productDetailTrackPageLoadService.reset();
    this.productChange$?.unsubscribe();
    this.toast$?.unsubscribe();
    this.personalizations$?.unsubscribe();
    this.favoritesListSubscription$?.unsubscribe();
  }

  async onTabSelection(tab: number) {
    this.isSellerCustomer = tab === 2;
    this.isCustomer = tab === 1;

    if (this.isSellerCustomer) {
      this.productSubscription$ = this.trackPageLoadAndSubstitutes();
      this.sellerPriceDetails$ = this.selectSellerPricingService.getSellerPrice(
        this.selectedProductNumber,
      );
      this.isPmeSgpEnabled$ = this.panAppState.feature$([
        FEATURES.split_dynamic_pme_sgp.name,
      ]);
      this.divisionalPromotions$ =
        this.divisionalPromotionsService.getDivisionalPromotions(
          this.selectedProductNumber,
        );
    } else if (this.isCustomer) {
      this.productSubscription$ = this.trackPageLoadAndSubstitutes();
      await this.renderPowerReviews();
    }
  }

  onQuantityUpdate(quantityUpdate: QuantityUpdate) {
    this.productDetailService.trackAndChangeQuantity(
      quantityUpdate,
      this.product,
      this.isSellerCustomer,
      this.productDetailTrackPageLoadService.getRatingStarts(),
      this.navigationHelperService?.getQueryParams()?.get('variant'),
    );
  }
  changeBetterBuysQuantityHandler(event: QuantityUpdate) {
    //TODO: Use OnQuantityUpdate as handler once tracking behavior is confirmed
    this.orderItemService.changeQuantity(
      event.productNumber,
      event.quantity,
      event.isEaches,
    );
  }

  buildShareOptions(): ShareOptions {
    return {
      title: PDP_SHARESHEET_CONSTANTS.GENERIC_INTRO,
      dialogTitle: PDP_SHARESHEET_CONSTANTS.GENERIC_TITLE, // android only
      text:
        PDP_SHARESHEET_CONSTANTS.GENERIC_INTRO +
        this.product?.summary?.productDescTxtl +
        '\n', // line break here may be pdp specific
      url: this.shareSheetService.buildDeeplink(
        PDP_SHARESHEET_CONSTANTS.PDP_DEEPLINK_ORIGIN,
        PDP_SHARESHEET_CONSTANTS.PDP_ORIGIN_LINK_TYPE,
        [PDP_SHARESHEET_CONSTANTS.PRODUCT_NUMBER + this.product.productNumber],
      ),
    } as ShareOptions;
  }

  onShareButtonPress() {
    if (
      this.platformService.platformType !==
      this.platformService.platformEnum.desktop
    ) {
      this.shareSheetService
        .share(this.buildShareOptions())
        .then(() => {
          const { divisionNumber, productNumber } = this.product?.summary;
          const { trackingAttributes } = this.product;
          this.productAnalyticsService.trackPDPShare(
            divisionNumber,
            productNumber,
            trackingAttributes,
          );
        })
        .catch(e => {
          if (e instanceof VersionError) {
            this.productDetailToastService.publishError(
              SHARESHEET_CONSTANTS.UPDATE_MSG,
            );
          } else if (e instanceof DeviceNotAvailableError) {
            this.productDetailToastService.publishError(
              SHARESHEET_CONSTANTS.DEVICE_NOT_AVAILABLE,
            );
          } else {
            this.productDetailToastService.publishError(
              SHARESHEET_CONSTANTS.ERROR_MSG,
            );
          }
        });
    } else {
      this.productDetailShareViaEmailService.showShareViaEmailModal(
        this.product,
      );
    }
  }

  onPrintButtonPress() {
    this.productDetailPrintService.showPrintModal(this.product);
  }

  trackPageLoadAndSubstitutes = () => {
    return this.product$?.pipe(take(1)).subscribe(product => {
      const { productNumber, trackingAttributes } = product;
      if (product?.alternative?.required && product?.alternative?.available) {
        const {
          productNumber: alternativeProdNumber,
          trackingAttributes: alternativeTrackingAttr,
        } = product.alternative.product;

        const parentProduct = {
          productNumber: productNumber,
          attributes: trackingAttributes,
        } as ProductTrackingInfo;

        const alternativeProduct = {
          productNumber: alternativeProdNumber,
          attributes: alternativeTrackingAttr,
        } as ProductTrackingInfo;

        if (product.alternative.substituteInfo) {
          alternativeProduct.substituteInfo =
            product.alternative.substituteInfo;
        }

        this.productDetailService.trackSubstituteProduct(
          alternativeProduct,
          parentProduct,
          this.isSellerCustomer,
        );
        if (!this.isSellerCustomer) {
          this.productDetailTrackPageLoadService.trackFeatureLoad(
            productNumber,
            trackingAttributes,
            product?.betterBuy?.product?.productNumber,
            product.betterBuy?.product?.trackingAttributes,
          );
        }
      } else {
        if (!this.isSellerCustomer) {
          this.productDetailService.trackPageLoad(
            productNumber,
            trackingAttributes,
            product?.betterBuy?.product?.productNumber,
            product.betterBuy?.product?.trackingAttributes,
            product?.pricing,
          );
          this.productDetailTrackPageLoadService.trackFeatureLoad(
            productNumber,
            trackingAttributes,
            product?.betterBuy?.product?.productNumber,
            product.betterBuy?.product?.trackingAttributes,
          );
        } else {
          this.productDetailService.trackSellerDetailPageLoad(
            this.selectedProductNumber,
            this.mainProductAttributesList,
          );
        }
      }
    });
  };

  renderPowerReviews(): Promise<void> {
    return new Promise(res => {
      // build the options extending the base configuration
      const options: PwrRenderOptions = {
        ...this.powerReviewService.getBaseRenderOptions(),
        page_id: `${this.selectedProductNumber}`,
        product: this.powerReviewService.buildFeedlessProduct(this.product),
        components: {
          ReviewSnippet:
            'pwr-product-details-review-snippet-' + this.selectedProductNumber,

          ReviewDisplay:
            'pwr-product-details-reviews-' + this.selectedProductNumber,
          ...(this.product?.summary?.isExclusiveBrand && {
            QuestionDisplay:
              'pwr-product-details-questions-' + this.selectedProductNumber,

            QuestionSnippet:
              'pwr-product-details-questions-snippet-' +
              this.selectedProductNumber,
          }),
        },

        on_render: (config, data: PwrRenderOptionsData) => {
          PowerReviewHandlerCssService.override();
          this.powerReviewAnalyticsService.bindReviewsEvents(this.product);
          this.powerReviewAnalyticsService.bindQAEvents(this.product);

          this.productDetailTrackPageLoadService.nextRatingStarts(
            data?.average_rating,
          );
          this.productDetailTrackPageLoadService.nextQuestion(
            data?.question_count,
          );
          this.pwrRenderOptionsData = data;

          const pwrSnapshot = Array.from(
            document.getElementsByClassName('pr-review-snapshot'),
          ).pop();

          const snapshotRecoMessage = pwrSnapshot?.getElementsByClassName(
            'pr-reco-to-friend-message',
          )[0];

          const snapshotReviewCount = pwrSnapshot?.getElementsByClassName(
            'pr-snippet-review-count',
          )[0];

          if (snapshotRecoMessage) {
            snapshotRecoMessage.textContent = 'would recommend';
          }

          if (snapshotReviewCount) {
            this.appendDisclaimer('pr-review-snapshot');
            // remove ( or ) characters
            var count = parseInt(
              snapshotReviewCount.textContent.replace(/\(|\)/gm, ``),
            );
            snapshotReviewCount.textContent =
              count > 1 ? count + ' Reviews' : count + ' Review';
          } else {
            this.appendDisclaimer('pr-review-display');
          }

          const pwrQASearch = document.getElementsByClassName(
            'pr-qa-display-search',
          )[0];

          if (pwrQASearch) {
            pwrQASearch.getElementsByTagName('label')[0].textContent =
              'Have a question? Search for answers';
            pwrQASearch.getElementsByTagName('input')[0].placeholder =
              'Enter Search Terms';
          }
          // Ask Questions EmptyState
          if (data?.question_count === 0) {
            this.noQuestions = true;
            const noQuestionsTitle = document
              .getElementsByClassName('pr-no-qa')[0]
              ?.getElementsByClassName('pr-ask-question-cta')[0];
            noQuestionsTitle.innerHTML =
              "Don't see the information you're looking for? Be the first to ask!";
            this.createTypicalQuestionsSection();
          }
        },
        on_back_to_top_click: function () {
          const prReviewElement = document.getElementById('pr-review-display');
          prReviewElement.scrollIntoView({ behavior: 'smooth' });
        },
      };

      setTimeout(() => {
        this.powerReviewService.renderComponents(options);
        res();
      }, 500);
    });
  }

  appendDisclaimer(elementId: string) {
    const disclaimerId = `disclaimer-${this.selectedProductNumber}`;

    if (!document.getElementById(disclaimerId)) {
      const paragraph = document.createElement('p');
      paragraph.setAttribute('id', disclaimerId);
      const text = document.createTextNode(
        'Because US Foods values your time spent reviewing our products, we frequently offer eligible customers a chance to win a prize by submitting an honest review. Please look out for emails with these offers, which support the majority of our reviews.',
      );
      paragraph.appendChild(text);
      paragraph.classList.add('snapshot-disclaimer');
      if (elementId === 'pr-review-display') {
        paragraph.classList.add('disclaimer-pwr-review-display');
      } else {
        paragraph.classList.add('disclaimer-pwr-snapshot');
      }
      const pwrChild = document.getElementById(elementId);
      const snapshotFaceoff = document.getElementsByClassName(
        'pr-review-snapshot-faceoff',
      )[0];
      if (!!snapshotFaceoff) {
        snapshotFaceoff.insertAdjacentElement('beforebegin', paragraph);
        return;
      }
      if (!!pwrChild) {
        pwrChild.insertAdjacentElement('afterend', paragraph);
      }
    }
  }

  createTypicalQuestionsSection() {
    // create Default Questions Section
    const typicalQuestionsId = `typical-questions-${this.selectedProductNumber}`;
    const pwrQuestionsProduct = document.getElementById(
      `pwr-product-details-questions-${this.selectedProductNumber}`,
    );

    if (!document.getElementById(typicalQuestionsId)) {
      const typicalQuestionsSection = document.createElement('section');
      typicalQuestionsSection.setAttribute('id', typicalQuestionsId);
      typicalQuestionsSection.classList.add('typical-questions');
      // Typical questions asked about products:
      typicalQuestionsSection.innerHTML = `
    <p>Typical questions asked about products:</p>
    <ul>
    <li><span>Is this product already seasoned?</span></li>
    <li><span>Is this item easy to use?</span></li>
    <li><span>What are the dimensions of this item?</span></li>
    </ul>
    `;
      pwrQuestionsProduct?.appendChild(typicalQuestionsSection);
    }
  }

  async openHelpModal() {
    if (this.platformService.isTouch.value) {
      this.modalService.setModalOptions(true, ModalWithNavComponent, {
        rootPage: HelpModalTouchComponent,
      });
    } else {
      this.modalService.setModalOptions(
        false,
        ModalWithNavComponent,
        {
          rootPage: HelpModalDesktopComponent,
        },
        'modal-desktop-size-xl',
      );
    }

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();
  }

  acceptSwap() {
    const product = {
      ...this.product,
    } as Product;

    this.betterBuysService.openAcceptSwapModal(
      product,
      false,
      BetterBuysConstants.productCapability,
    );
  }

  trackProductImageClick(product: Product) {
    this.productAnalyticsService.trackProductImageClick(
      product?.summary?.divisionNumber,
      product?.productNumber,
      product?.trackingAttributes,
      product?.resultRank,
    );
  }

  handleProductImageClick(betterBuyProduct: ProductDetailViewModel) {
    this.trackProductImageClick(betterBuyProduct);
    this.navigationHelperService.routeToProductDetails(
      betterBuyProduct.productNumber,
    );
  }

  loadFavoritesList() {
    this.isFavoriteButtonEnabled$ =
      this.productDetailService.shouldEnableFavoriteButton();
    this.favoritesListSubscription$ = this.productDetailService
      .getFavoritesList()
      .subscribe(favoritesList => {
        if (favoritesList) {
          this.favoritesList = favoritesList.list;
          if (!favoritesList.isLoading) {
            this.updateFavoriteStatus();
            this.isUpdatingFavoriteList = false;
          }
        }
      });
  }

  updateFavoriteStatus() {
    if (!this.isUpdatingFavoriteList) {
      this.isFavorite = this.favoritesList.listItems.some(
        item => item.productNumber === this.selectedProductNumber,
      );
    }
  }

  onFavoriteButtonPress(favorite: boolean) {
    this.isFavorite = favorite;
    this.isUpdatingFavoriteList = true;

    if (favorite) {
      this.productDetailService.addProductToFavoritesList(
        this.product,
        this.favoritesList,
      );
    } else {
      this.productDetailService.removeProductFromFavoritesList(
        this.product,
        this.favoritesList,
      );
    }
  }
}
